<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import api from "@/axios";
import { getBaseUrl } from "@/helpers/baseUrl";

export default {
  components: { Layout, PageHeader },

  data() {
    return {
      title: "Atualizações do Sistema",
      items: [
        { text: "Administrativo", href: "/" },
        { text: "Atualizações do Sistema", active: true },
      ],
      releases: [],
      total: 0,
      page: 1,
      pages: 1,
      size: 10,
      sizes: [10, 20, 50],
      dtIniFiltro: null,
      dtFimFiltro: null,
      loading: false,
      erroCarregamento: null,
    };
  },

  mounted() {
    const hoje = new Date();
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(hoje.getDate() - 30);
    this.dtIniFiltro = trintaDiasAtras.toISOString().slice(0, 10);
    this.dtFimFiltro = hoje.toISOString().slice(0, 10);
    this.carregar();
  },

  methods: {
    async carregar() {
      this.loading = true;
      this.erroCarregamento = null;
      try {
        const res = await api.get(`${getBaseUrl()}api/releases-timeline`, {
          params: {
            page: this.page,
            size: this.size,
            di:   this.dtIniFiltro,
            df:   this.dtFimFiltro,
          },
        });
        this.releases = res.data.data  ?? [];
        this.total    = res.data.total ?? 0;
        this.pages    = res.data.pages ?? 1;
      } catch (e) {
        this.erroCarregamento = "Não foi possível carregar as atualizações.";
      } finally {
        this.loading = false;
      }
    },

    filtrar() {
      this.page = 1;
      this.carregar();
    },

    irPagina(p) {
      if (p < 1 || p > this.pages) return;
      this.page = p;
      this.carregar();
    },

    alterarSize() {
      this.page = 1;
      this.carregar();
    },

    badgeClass(categoria) {
      const map = {
        nova_funcionalidade: "badge bg-success",
        correcao_bug:        "badge bg-danger",
        melhoria:            "badge bg-primary",
        refatoracao:         "badge bg-warning text-dark",
        outro:               "badge bg-secondary",
      };
      return map[categoria] ?? "badge bg-secondary";
    },

    badgeLabel(categoria) {
      const map = {
        nova_funcionalidade: "Nova Funcionalidade",
        correcao_bug:        "Correção de Bug",
        melhoria:            "Melhoria",
        refatoracao:         "Refatoração",
        outro:               "Outro",
      };
      return map[categoria] ?? categoria;
    },

    formatarData(iso) {
      if (!iso) return "—";
      return new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Filtros -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="card">
          <div class="card-body py-2">
            <div class="row align-items-end g-2">
              <div class="col-auto">
                <label class="form-label mb-1 small">Data inicial</label>
                <input
                  type="date"
                  class="form-control form-control-sm"
                  v-model="dtIniFiltro"
                />
              </div>
              <div class="col-auto">
                <label class="form-label mb-1 small">Data final</label>
                <input
                  type="date"
                  class="form-control form-control-sm"
                  v-model="dtFimFiltro"
                />
              </div>
              <div class="col-auto">
                <button class="btn btn-primary btn-sm" @click="filtrar" :disabled="loading">
                  <i class="bx bx-search me-1"></i>Filtrar
                </button>
              </div>
              <div class="col-auto ms-auto text-muted small align-self-center">
                {{ total }} registro(s) encontrado(s)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">

        <div v-if="erroCarregamento" class="alert alert-danger">
          {{ erroCarregamento }}
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Carregando...</span>
          </div>
        </div>

        <div v-else-if="releases.length === 0" class="text-center text-muted py-5">
          <i class="bx bx-git-commit fs-1"></i>
          <p class="mt-2">Nenhuma atualização encontrada para o período selecionado.</p>
        </div>

        <!-- Timeline -->
        <div v-else class="timeline-container">
          <div
            v-for="(release, index) in releases"
            :key="release._id ?? index"
            class="timeline-item"
          >
            <div class="timeline-line"></div>
            <div class="timeline-dot">
              <i class="bx bx-git-commit"></i>
            </div>

            <div class="card shadow-sm timeline-card">
              <div class="card-body">
                <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                  <h5 class="card-title mb-0 fw-semibold">{{ release.titulo }}</h5>
                  <span :class="badgeClass(release.categoria)">
                    {{ badgeLabel(release.categoria) }}
                  </span>
                </div>

                <p class="card-text text-muted mb-3">{{ release.descricao }}</p>

                <div class="d-flex flex-wrap gap-3 text-muted small">
                  <span><i class="bx bx-user me-1"></i>{{ release.author_name ?? "—" }}</span>
                  <span><i class="bx bx-git-repo-forked me-1"></i>{{ release.repo ?? "—" }}</span>
                  <span><i class="bx bx-package me-1"></i>{{ release.commits_count ?? 0 }} commit(s)</span>
                  <span><i class="bx bx-file me-1"></i>{{ (release.files_changed ?? []).length }} arquivo(s)</span>
                  <span class="ms-auto"><i class="bx bx-time me-1"></i>{{ formatarData(release.pushed_at) }}</span>
                </div>

                <div v-if="release.files_changed && release.files_changed.length" class="mt-3">
                  <details>
                    <summary class="text-muted small" style="cursor:pointer">Ver arquivos alterados</summary>
                    <ul class="list-unstyled mt-2 mb-0">
                      <li
                        v-for="(arq, i) in release.files_changed"
                        :key="i"
                        class="text-muted small font-monospace"
                      >
                        <i class="bx bx-code-alt me-1"></i>{{ arq }}
                      </li>
                    </ul>
                  </details>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Paginação -->
        <div v-if="!loading && releases.length > 0" class="d-flex align-items-center justify-content-between mt-3 mb-4">
          <div class="d-flex align-items-center gap-2">
            <label class="small text-muted mb-0">Registros por página:</label>
            <select class="form-select form-select-sm w-auto" v-model="size" @change="alterarSize">
              <option v-for="s in sizes" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>

          <div class="d-flex align-items-center gap-2">
            <span class="small text-muted">Página {{ page }} de {{ pages }}</span>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page <= 1"
              @click="irPagina(page - 1)"
            >
              <i class="bx bx-chevron-left"></i>
            </button>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page >= pages"
              @click="irPagina(page + 1)"
            >
              <i class="bx bx-chevron-right"></i>
            </button>
          </div>
        </div>

      </div>
    </div>
  </Layout>
</template>

<style scoped>
.timeline-container {
  position: relative;
  padding-left: 40px;
}

.timeline-item {
  position: relative;
  margin-bottom: 24px;
}

.timeline-line {
  position: absolute;
  left: -28px;
  top: 0;
  bottom: -24px;
  width: 2px;
  background: #dee2e6;
}

.timeline-item:last-child .timeline-line {
  display: none;
}

.timeline-dot {
  position: absolute;
  left: -38px;
  top: 12px;
  width: 22px;
  height: 22px;
  background: #556ee6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  z-index: 1;
}

.timeline-card {
  border-left: 3px solid #556ee6;
}
</style>
